import { makeRng } from "../rng.js";
import type { Vec2 } from "../types.js";
import type {
  ConvoyConfig,
  ConvoyEvent,
  ConvoyFrame,
  ConvoyHeights,
  ConvoyResult,
  ConvoySummary,
  OverwatchSpec,
} from "./types.js";
import { buildConvoyWorld, losHeightClear, pointInPoly } from "./world.js";

export const CONVOY_DT = 0.05;
export const CONVOY_FRAME_DT = 0.1;

/** Armed/sensor overwatch layer characteristics. */
export const OVERWATCH: OverwatchSpec = {
  range: 800,
  sensorRange: 1700,
  react: 1.0,
  interval: 1.2,
  pk: 0.7,
  mag: 10,
  channels: 2,
};

/**
 * Entity heights for the 2.5D line-of-sight model. A sight line is blocked
 * wherever it would pass through canopy. This makes the sensor horizon real:
 * a 3 m hull sensor never sees over a 20 m treeline, a 25 m mast sees over it
 * with a finite dead-ground skirt behind, and an overwatch platform at
 * altitude looks almost straight down past it.
 */
export const CONVOY_HEIGHTS: ConvoyHeights = {
  hullSensor: 3,
  mastSensor: 25,
  weapon: 2,
  droneCruise: 15,
  droneUnderCanopy: 4,
  droneCrestClearance: 5,
  droneClimbTime: 1.5,
  overwatchAlt: 400,
};
/** Time between follow-up shots from a vehicle fire channel, s. */
const SHOT_INTERVAL = 0.9;
/** Track-formation delay between detection and a usable fire solution, s. */
const DATALINK_DELAY = 0.2;
/** Drone warhead triggers inside this range, m. */
const IMPACT_RANGE = 7;
/** Hard stop for the sim clock, s. */
const MAX_T = 360;

export function defaultConvoyConfig(overrides: Partial<ConvoyConfig> = {}): ConvoyConfig {
  return {
    vehicles: 4,
    spacing: 60,
    speed: 40,
    range: 250,
    react: 1.2,
    pk: 0.65,
    mag: 6,
    channels: 1,
    sensor: 1200,
    standoff: 120,
    density: 0.6,
    canopy: 20,
    drones: 6,
    dspeed: 45,
    events: 1,
    axes: 1,
    targeting: "concentrate",
    topCover: "none",
    seed: 1,
    ...overrides,
  };
}

type Vehicle = {
  i: number;
  s: number;
  alive: boolean;
  ammo: number;
  eng: Engagement[];
  x: number;
  y: number;
  hx: number;
  hy: number;
};

type Engagement = { id: number; tReady: number; next: number };

type Drone = {
  id: number;
  x: number;
  y: number;
  /** Current altitude, m. */
  h: number;
  /** Index of the launch block in world.blocks (masked run is only there). */
  launchBlock: number;
  tgt: number;
  alive: boolean;
  tracked: boolean;
  tTrack: number;
  detRange: number;
  engaged: number;
  satFlag: boolean;
  tLaunch: number;
};

type Overwatch = { x: number; y: number; ang: number; ammo: number; eng: Engagement[]; armed: boolean };

const d2 = (a: Vec2, b: Vec2): number => {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
};
const dist = (a: Vec2, b: Vec2): number => Math.sqrt(d2(a, b));

/**
 * Run one convoy-protection engagement to completion before any rendering.
 * With `light` set, replay frames are skipped (used for counterfactual runs).
 */
export function runConvoySim(cfg: ConvoyConfig, opts: { light?: boolean } = {}): ConvoyResult {
  const light = opts.light ?? false;
  const world = buildConvoyWorld(cfg);
  const rng = makeRng(cfg.seed * 7919 + 13);
  const vMs = cfg.speed / 3.6;
  const dMs = cfg.dspeed;

  // 2.5D sensor model: the mast raises the sensor head above the canopy
  // (when canopy allows); the hull sensor never sees over it.
  const H = CONVOY_HEIGHTS;
  const sensorH = cfg.topCover === "mast" ? H.mastSensor : H.hullSensor;
  const canopy = cfg.canopy;

  // Group ambush sites by event arc position (needed to place the start line).
  type Group = { s: number; sites: typeof world.sites; done: boolean };
  const groups: Group[] = [];
  for (const site of world.sites) {
    let g = groups.find((gr) => Math.abs(gr.s - site.s) < 1);
    if (!g) {
      g = { s: site.s, sites: [], done: false };
      groups.push(g);
    }
    g.sites.push(site);
  }
  const firstS = Math.min(...groups.map((g) => g.s));
  const lastS = Math.max(...groups.map((g) => g.s));
  const tail = (cfg.vehicles - 1) * cfg.spacing;
  const startS = Math.max(firstS - 450, tail + 60);
  const endS = Math.min(world.length - 90, lastS + 480);

  const veh: Vehicle[] = [];
  for (let i = 0; i < cfg.vehicles; i++) {
    veh.push({ i, s: startS - i * cfg.spacing, alive: true, ammo: cfg.mag, eng: [], x: 0, y: 0, hx: 1, hy: 0 });
  }
  const hasOW = cfg.topCover === "uav" || cfg.topCover === "armed";
  // Drawn unconditionally so the RNG stream — and therefore every launch time
  // and Pk roll — is identical across top-cover variants of the same seed.
  // That is what makes the "same attack, same dice" counterfactual literal.
  const owAng = rng() * Math.PI * 2;
  const ow: Overwatch | null = hasOW
    ? { x: 0, y: 0, ang: owAng, ammo: OVERWATCH.mag, eng: [], armed: cfg.topCover === "armed" }
    : null;

  const drones: Drone[] = [];
  const events: ConvoyEvent[] = [];
  const frames: ConvoyFrame[] = [];
  const perEvent = Math.ceil(cfg.drones / groups.length);

  const metrics = {
    launched: 0,
    killedV: 0,
    killedO: 0,
    leakers: 0,
    shots: 0,
    owShots: 0,
    sat: 0,
    warn: [] as number[],
    firstContact: null as number | null,
  };
  let t = 0;
  let endTimer: number | null = null;

  const aliveVeh = (): Vehicle[] => veh.filter((v) => v.alive);
  const losDetect = (v: Vec2, d: Drone): boolean =>
    losHeightClear(v, sensorH, d, d.h, world.blocks, canopy);
  const losFire = (v: Vec2, d: Drone): boolean =>
    losHeightClear(v, H.weapon, d, d.h, world.blocks, canopy);
  const losOverwatch = (o: Vec2, d: Drone): boolean =>
    losHeightClear(o, H.overwatchAlt, d, d.h, world.blocks, canopy);
  // Drone altitude profile, the heart of the 2.5D model:
  //  1. Launch climb — the first seconds over the clearing are flown above the
  //     canopy. Elevated sensors (mast head above canopy, overwatch) get their
  //     early cue here; hull sensors stay blind behind the treeline.
  //  2. Masked run — through its own prepared launch block at nap-of-earth,
  //     under the canopy, invisible to everyone.
  //  3. In the open it cruises low; any OTHER woodland on the way must be
  //     crested above the canopy (you don't thread an unfamiliar wood at speed).
  const droneAlt = (d: { x: number; y: number; launchBlock: number; tLaunch: number }): number => {
    if (t < d.tLaunch + H.droneClimbTime) return canopy + H.droneCrestClearance;
    for (let i = 0; i < world.blocks.length; i++) {
      const poly = world.blocks[i];
      if (poly && pointInPoly(d, poly)) {
        return i === d.launchBlock ? H.droneUnderCanopy : canopy + H.droneCrestClearance;
      }
    }
    return H.droneCruise;
  };
  const pickTarget = (k: number): Vehicle | null => {
    const av = aliveVeh();
    if (av.length === 0) return null;
    if (cfg.targeting === "concentrate") return av[0] ?? null;
    return av[k % av.length] ?? null;
  };

  const frameEvery = Math.round(CONVOY_FRAME_DT / CONVOY_DT);
  let step = 0;

  while (t < MAX_T) {
    // Move vehicles along the road.
    for (const v of veh) {
      if (v.alive) v.s += vMs * CONVOY_DT;
      const rp = world.posAt(v.s);
      v.x = rp.x;
      v.y = rp.y;
      v.hx = rp.tx;
      v.hy = rp.ty;
    }
    const av0 = aliveVeh();
    const front = av0.length > 0 ? Math.max(...av0.map((v) => v.s)) : -1;
    // Overwatch anchors on the survivors: wrecks are frozen on the road, and
    // averaging them in would drag the orbit back toward the kill site while
    // the rest of the column drives on.
    const anchor = av0.length > 0 ? av0 : veh;
    const cx = anchor.reduce((s, v) => s + v.x, 0) / anchor.length;
    const cy = anchor.reduce((s, v) => s + v.y, 0) / anchor.length;
    if (ow) {
      ow.ang += CONVOY_DT * 0.5;
      ow.x = cx + Math.cos(ow.ang) * 170;
      ow.y = cy + Math.sin(ow.ang) * 170;
    }

    // Ambush triggers: launch when the lead vehicle closes on the event arc.
    for (const g of groups) {
      if (g.done) continue;
      if (front >= 0 && front >= g.s - 160) {
        g.done = true;
        // One launch marker per treeline, so the replay can annotate where
        // the drones are coming up from.
        for (const site of g.sites) {
          events.push({ t, type: "launch", x: site.cx, y: site.cy });
        }
        let k = 0;
        for (let j = 0; j < perEvent && metrics.launched < cfg.drones; j++) {
          const site = g.sites[j % g.sites.length];
          const tgt = pickTarget(metrics.launched);
          if (!site || !tgt) break;
          const sx = site.cx + (rng() * 2 - 1) * site.r * 0.5;
          const sy = site.cy + (rng() * 2 - 1) * site.r * 0.5;
          const tLaunch = t + rng() * 0.8 + (cfg.axes === 2 ? (k % 2) * 0.4 : 0);
          const launchBlock = world.blocks.indexOf(site.poly);
          drones.push({
            id: drones.length,
            x: sx,
            y: sy,
            h: droneAlt({ x: sx, y: sy, launchBlock, tLaunch }),
            launchBlock,
            tgt: tgt.i,
            alive: true,
            tracked: false,
            tTrack: 0,
            detRange: 0,
            engaged: 0,
            satFlag: false,
            tLaunch,
          });
          metrics.launched++;
          k++;
        }
      }
    }

    // Move drones; resolve impacts.
    for (const d of drones) {
      if (!d.alive || t < d.tLaunch) continue;
      let tv = veh[d.tgt];
      if (!tv) continue;
      if (!tv.alive) {
        const av = aliveVeh();
        const first = av[0];
        if (!first) {
          d.alive = false;
          continue;
        }
        tv = av.reduce((best, v) => (d2(d, v) < d2(d, best) ? v : best), first);
        d.tgt = tv.i;
      }
      const dd = dist(d, tv);
      if (dd < IMPACT_RANGE) {
        d.alive = false;
        metrics.leakers++;
        tv.alive = false;
        tv.eng = [];
        events.push({ t, type: "impact", x: tv.x, y: tv.y });
        if (d.tracked) metrics.warn.push(d.detRange / dMs);
        continue;
      }
      const ux = (tv.x - d.x) / dd;
      const uy = (tv.y - d.y) / dd;
      d.x += ux * dMs * CONVOY_DT;
      d.y += uy * dMs * CONVOY_DT;
      // Terrain-mask under the canopy while inside a block; cruise low outside.
      d.h = droneAlt(d);
    }

    // Detection / track formation.
    for (const d of drones) {
      if (!d.alive || d.tracked || t < d.tLaunch) continue;
      let det = false;
      if (ow && dist(d, ow) < OVERWATCH.sensorRange && losOverwatch(ow, d)) det = true;
      if (!det) {
        for (const v of veh) {
          if (!v.alive) continue;
          if (d2(d, v) < cfg.sensor * cfg.sensor && losDetect(v, d)) {
            det = true;
            break;
          }
        }
      }
      if (det) {
        d.tracked = true;
        d.tTrack = t + DATALINK_DELAY;
        const tv = veh[d.tgt];
        d.detRange = tv ? dist(d, tv) : 0;
        if (metrics.firstContact === null) metrics.firstContact = t;
      }
    }

    // Vehicle engagements.
    for (const v of veh) {
      if (!v.alive) continue;
      v.eng = v.eng.filter((e) => {
        const d = drones[e.id];
        return d !== undefined && d.alive && d2(d, v) <= cfg.range * cfg.range * 1.2;
      });
      if (v.ammo > 0 && v.eng.length < cfg.channels) {
        const cands = drones
          .filter(
            (d) =>
              d.alive &&
              d.tracked &&
              t >= d.tTrack &&
              d2(d, v) <= cfg.range * cfg.range &&
              losFire(v, d) &&
              !v.eng.some((e) => e.id === d.id),
          )
          .sort((a, b) => a.engaged - b.engaged || d2(a, v) - d2(b, v));
        while (v.eng.length < cfg.channels && cands.length > 0 && v.ammo > 0) {
          const d = cands.shift();
          if (!d) break;
          d.engaged++;
          v.eng.push({ id: d.id, tReady: Math.max(t, d.tTrack) + cfg.react, next: 0 });
        }
      }
      for (const e of v.eng) {
        const d = drones[e.id];
        if (!d) continue;
        if (t >= e.tReady && t >= e.next && v.ammo > 0) {
          v.ammo--;
          metrics.shots++;
          e.next = t + SHOT_INTERVAL;
          events.push({ t, type: "shot", x1: v.x, y1: v.y, x2: d.x, y2: d.y });
          if (rng() < cfg.pk) {
            d.alive = false;
            metrics.killedV++;
            metrics.warn.push(d.detRange / dMs);
            events.push({ t, type: "kill", x: d.x, y: d.y });
          }
        }
      }
      v.eng = v.eng.filter((e) => drones[e.id]?.alive);
    }

    // Armed overwatch engagements.
    if (ow && ow.armed) {
      ow.eng = ow.eng.filter((e) => {
        const d = drones[e.id];
        return d !== undefined && d.alive && d2(d, ow) <= OVERWATCH.range * OVERWATCH.range * 1.3;
      });
      if (ow.ammo > 0 && ow.eng.length < OVERWATCH.channels) {
        const cands = drones
          .filter(
            (d) =>
              d.alive &&
              d.tracked &&
              t >= d.tTrack &&
              d2(d, ow) <= OVERWATCH.range * OVERWATCH.range &&
              losOverwatch(ow, d) &&
              !ow.eng.some((e) => e.id === d.id),
          )
          .sort((a, b) => d2(a, ow) - d2(b, ow));
        while (ow.eng.length < OVERWATCH.channels && cands.length > 0 && ow.ammo > 0) {
          const d = cands.shift();
          if (!d) break;
          ow.eng.push({ id: d.id, tReady: Math.max(t, d.tTrack) + OVERWATCH.react, next: 0 });
        }
      }
      for (const e of ow.eng) {
        const d = drones[e.id];
        if (!d) continue;
        if (t >= e.tReady && t >= e.next && ow.ammo > 0) {
          ow.ammo--;
          metrics.owShots++;
          e.next = t + OVERWATCH.interval;
          events.push({ t, type: "owshot", x1: ow.x, y1: ow.y, x2: d.x, y2: d.y });
          if (rng() < OVERWATCH.pk) {
            d.alive = false;
            metrics.killedO++;
            metrics.warn.push(d.detRange / dMs);
            events.push({ t, type: "kill", x: d.x, y: d.y });
          }
        }
      }
      ow.eng = ow.eng.filter((e) => drones[e.id]?.alive);
    }

    // Saturation: a tracked drone deep inside engagement range that nobody can service.
    for (const d of drones) {
      if (!d.alive || d.satFlag || !d.tracked) continue;
      const tv = veh[d.tgt];
      if (!tv || !tv.alive) continue;
      if (d2(d, tv) < cfg.range * cfg.range * 0.7) {
        const beingEngaged =
          veh.some((v) => v.eng.some((e) => e.id === d.id)) ||
          (ow !== null && ow.armed && ow.eng.some((e) => e.id === d.id));
        if (!beingEngaged) {
          const freeChannel =
            veh.some(
              (v) => v.alive && v.ammo > 0 && v.eng.length < cfg.channels && d2(d, v) <= cfg.range * cfg.range,
            ) ||
            (ow !== null &&
              ow.armed &&
              ow.ammo > 0 &&
              ow.eng.length < OVERWATCH.channels &&
              d2(d, ow) <= OVERWATCH.range * OVERWATCH.range);
          if (!freeChannel) {
            d.satFlag = true;
            metrics.sat++;
          }
        }
      }
    }

    // Replay frame capture.
    if (!light && step % frameEvery === 0) {
      frames.push({
        t: +t.toFixed(2),
        veh: veh.map((v) => ({ x: v.x, y: v.y, hx: v.hx, hy: v.hy, a: v.alive ? 1 : 0, m: v.ammo })),
        dr: drones.map((d) => ({
          x: d.x,
          y: d.y,
          h: +d.h.toFixed(1),
          a: d.alive && t >= d.tLaunch ? 1 : 0,
          tr: d.tracked ? 1 : 0,
        })),
        ow: ow ? { x: ow.x, y: ow.y } : null,
      });
    }

    // End conditions.
    const dronesPending = drones.some((d) => d.alive) || groups.some((g) => !g.done);
    const allDead = !veh.some((v) => v.alive);
    if (front > endS && !dronesPending) break;
    if (allDead) {
      if (endTimer === null) endTimer = t + 3;
      if (t > endTimer) break;
    }
    t += CONVOY_DT;
    step++;
  }

  const lost = veh.filter((v) => !v.alive).length;
  const summary: ConvoySummary = {
    vehicles: cfg.vehicles,
    lost,
    intactPct: Math.round(((cfg.vehicles - lost) / cfg.vehicles) * 100),
    launched: metrics.launched,
    killedV: metrics.killedV,
    killedO: metrics.killedO,
    leakers: metrics.leakers,
    shots: metrics.shots,
    owShots: metrics.owShots,
    sat: metrics.sat,
    realPk: metrics.shots > 0 ? metrics.killedV / metrics.shots : 0,
    ammoLeft: veh.reduce((s, v) => s + v.ammo, 0),
    warnMean: metrics.warn.length > 0 ? metrics.warn.reduce((a, b) => a + b, 0) / metrics.warn.length : 0,
    warnMin: metrics.warn.length > 0 ? Math.min(...metrics.warn) : 0,
    firstContact: metrics.firstContact,
  };
  const { posAt: _posAt, ...publicWorld } = world;
  return { world: publicWorld, frames, events, summary, config: { ...cfg } };
}
